import { FC, useEffect, useState } from "react";
import type { Doc } from "sharedb/lib/client";

import type { WorksheetType } from "../../../../../shared/types/worksheet";
import { BLOCKS_PATH } from "../../../../../shared/worksheets/constants";
import { blockIsAsset, getEditableBlocks } from "../../../../../shared/worksheets/doc-helpers";

import { FeatureAssetHeader } from "./FeatureAssetHeader";
import { FeatureBreadcrumbs } from "./FeatureBreadcrumbs";
import { FeatureSimpleHeader } from "./FeatureSimpleHeader";

import { useNameFromPageAccessId } from "components/materials/page/helpers";
import type { PageType } from "components/server-types";
import { FeatureStatus, usePresentation } from "utils/presentation";

export type FeatureHeaderProps = {
  doc: Doc<WorksheetType>;
  page: PageType;
};

export const FeatureHeader: FC<FeatureHeaderProps> = ({ doc, page }) => {
  const {
    featureStatus,
    presentedSessionId,
    presentedPageId: pageId,
    isPresenting,
    presentedAccessId,
    presentedBlockId,
    presentingRollup,
  } = usePresentation();

  const [originatingStatus, setOriginatingStatus] = useState<FeatureStatus | null>();

  const blocks = presentingRollup ? getEditableBlocks(doc) : doc?.data?.[BLOCKS_PATH];
  const presentedBlockIndex = blocks?.findIndex((b) => b.id === presentedBlockId);
  const presentedBlock = blocks?.[presentedBlockIndex];
  const responseName = useNameFromPageAccessId(presentedAccessId, pageId);

  // Set the singleUserResponses state when the featureStatus changes
  useEffect(() => {
    // Showing an individual response should not set the singleUserResponses state
    // as both paths have the individual response state
    if (featureStatus !== FeatureStatus.PARTICIPANT_RESPONSE_WITH_BLOCK) {
      setOriginatingStatus(featureStatus);
    }
  }, [featureStatus]);

  // Should never happen
  if (!presentedSessionId) {
    return null;
  }

  if (presentedBlockId && blockIsAsset(doc, presentedBlockId)) {
    return <FeatureAssetHeader blockIndex={presentedBlockIndex} doc={doc} />;
  }

  const showSimpleHeader = !isPresenting || !presentingRollup || !presentedBlockId;

  if (showSimpleHeader) {
    return (
      <FeatureSimpleHeader
        anonymizeResponses={!!page.anonymize_student_responses}
        blockType={presentedBlock?.t}
        featureStatus={featureStatus}
        isPresenting={isPresenting}
        responseName={responseName}
      />
    );
  }

  return (
    <FeatureBreadcrumbs
      classSessionId={presentedSessionId}
      featureBlockId={presentedBlockId}
      featureStatus={featureStatus}
      page={page}
      showSingleQuestionStep={originatingStatus === FeatureStatus.CLASS_RESPONSES_WITH_BLOCK}
    />
  );
};
